/**
 * Funções de formatação para a Central de Atendimento
 */

/**
 * Normaliza telefone para formato canônico (12 dígitos BR, sem o "9" extra
 * de celular). Usado para comparar `selectedAccount.phone` com `wa_notify.to`
 * — se o salvo e o recebido divergem em 1 dígito, o real-time silenciosamente
 * falha.
 * @param {string} phone - Telefone
 * @returns {string} Telefone normalizado
 */
export const normalizePhone = (phone) => {
  if (!phone) return "";
  let digits = String(phone).replace(/\D/g, "");

  // Brasil: 55 + DDD(2) + número. Celular = 9 dígitos (com o "9" inicial),
  // fixo = 8. Remove o "9" extra para padronizar em 12 dígitos.
  // Ex: 5547996897795 (13) → 554796897795 (12)
  if (digits.length === 13 && digits.startsWith("55")) {
    const ddd = digits.slice(2, 4);
    const rest = digits.slice(4);
    if (rest.length === 9 && rest.startsWith("9")) {
      digits = `55${ddd}${rest.slice(1)}`;
    }
  }
  return digits;
};

/**
 * Formata telefone para exibição
 * @param {string} phone - Telefone
 * @returns {string} Telefone formatado
 */
export const formatPhone = (phone) => {
  if (!phone) return "";

  const cleaned = normalizePhone(phone);

  // Formato brasileiro: +55 (XX) XXXXX-XXXX
  if (cleaned.length === 13 && cleaned.startsWith("55")) {
    const ddd = cleaned.slice(2, 4);
    const part1 = cleaned.slice(4, 9);
    const part2 = cleaned.slice(9);
    return `+55 (${ddd}) ${part1}-${part2}`;
  }

  // Formato brasileiro sem código do país
  if (cleaned.length === 11) {
    const ddd = cleaned.slice(0, 2);
    const part1 = cleaned.slice(2, 7);
    const part2 = cleaned.slice(7);
    return `(${ddd}) ${part1}-${part2}`;
  }

  return phone;
};

/**
 * Formata data para exibição relativa
 * @param {string|number|Date} date - Data
 * @returns {string} Data formatada
 */
export const formatRelativeDate = (date) => {
  if (!date) return "";

  const now = new Date();
  const targetDate = new Date(date);
  const diffMs = now - targetDate;
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return "Agora";
  if (diffMins < 60) return `${diffMins}min`;
  if (diffHours < 24) return `${diffHours}h`;
  if (diffDays === 1) return "Ontem";
  if (diffDays < 7) return `${diffDays} dias`;

  return targetDate.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
  });
};

/**
 * Formata data completa
 * @param {string|number|Date} date - Data
 * @returns {string} Data formatada
 */
export const formatFullDate = (date) => {
  if (!date) return "";

  const targetDate = new Date(date);
  return targetDate.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Formata data para separador de mensagens
 * @param {string|number|Date} date - Data
 * @returns {string} Data formatada
 */
export const formatMessageDate = (date) => {
  if (!date) return "";

  const now = new Date();
  const targetDate = new Date(date);

  const isToday = targetDate.toDateString() === now.toDateString();

  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = targetDate.toDateString() === yesterday.toDateString();

  if (isToday) return "Hoje";
  if (isYesterday) return "Ontem";

  return targetDate.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

/**
 * Formata hora da mensagem
 * @param {string|number|Date} date - Data
 * @returns {string} Hora formatada
 */
export const formatMessageTime = (date) => {
  if (!date) return "";

  let targetDate;

  if (typeof date === "number") {
    // Timestamp em segundos ou milissegundos
    targetDate = new Date(date > 1000000000000 ? date : date * 1000);
  } else {
    targetDate = new Date(date);
  }

  return targetDate.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Formata duração
 * @param {number} minutes - Duração em minutos
 * @returns {string} Duração formatada
 */
export const formatDuration = (minutes) => {
  if (!minutes || minutes < 1) return "< 1min";

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours === 0) return `${mins}min`;
  if (mins === 0) return `${hours}h`;

  return `${hours}h ${mins}min`;
};

/**
 * Trunca texto com reticências
 * @param {string} text - Texto
 * @param {number} maxLength - Tamanho máximo
 * @returns {string} Texto truncado
 */
export const truncateText = (text, maxLength = 30) => {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + "...";
};

/**
 * Formata preview da última mensagem
 * @param {string} text - Texto da mensagem
 * @returns {string} Preview formatado
 */
export const formatLastMessagePreview = (text) => {
  if (!text) return "";

  // Verificar se é JSON de mídia
  try {
    const parsed = JSON.parse(text);
    if (parsed.image?.url) return "📷 Imagem";
    if (parsed.audio?.url) return "🎤 Áudio";
    if (parsed.document?.url) return "📄 Documento";
    if (parsed.video?.url) return "🎥 Vídeo";
  } catch {
    // Não é JSON, continuar
  }

  // Verificar prefixos
  if (text.startsWith("image:")) return "📷 Imagem";
  if (text.startsWith("audio:")) return "🎤 Áudio";
  if (text.startsWith("pdf:")) return "📄 Documento";

  // Texto normal
  return truncateText(text.replace(/\n/g, " "), 35);
};

/**
 * Gera iniciais do nome
 * @param {string} name - Nome completo
 * @returns {string} Iniciais
 */
export const getInitials = (name) => {
  if (!name) return "?";

  const parts = name.trim().split(" ");
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }

  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

/**
 * Gera URL do avatar fallback
 * @param {string} name - Nome para gerar avatar
 * @returns {string} URL do avatar
 */
export const getAvatarUrl = (name) => {
  const encoded = encodeURIComponent(name || "User");
  return `https://ui-avatars.com/api/?name=${encoded}&background=random&color=fff&bold=true`;
};

/**
 * Converte timestamp para Date
 * @param {string|number} timestamp - Timestamp
 * @returns {Date} Objeto Date
 */
export const timestampToDate = (timestamp) => {
  if (!timestamp) return new Date();

  if (typeof timestamp === "string") {
    return new Date(timestamp);
  }

  // Se for número, verificar se está em segundos ou milissegundos
  if (typeof timestamp === "number") {
    return new Date(timestamp > 1000000000000 ? timestamp : timestamp * 1000);
  }

  return new Date(timestamp);
};

/**
 * Agrupa mensagens por data
 * @param {Array} messages - Lista de mensagens
 * @returns {Array} Mensagens com separadores de data
 */
export const addDateSeparators = (messages) => {
  if (!messages || messages.length === 0) return [];

  const result = [];
  let lastDate = null;

  messages.forEach((message) => {
    const messageDate = timestampToDate(message.timestamp);
    const dateString = messageDate.toDateString();

    if (dateString !== lastDate) {
      result.push({
        id: `date-separator-${dateString}-${Date.now()}`,
        type: "dateSeparator",
        dateText: formatMessageDate(messageDate),
        timestamp: messageDate.getTime(),
      });
      lastDate = dateString;
    }

    result.push(message);
  });

  return result;
};

/**
 * Formata data para o separador de datas no chat
 * @param {string|Date} date - Data
 * @returns {string} Data formatada
 */
export const formatDateSeparator = (date) => {
  if (!date) return "";
  return formatMessageDate(date);
};

/**
 * Formata tempo relativo (ex: "há 5 minutos")
 * @param {string|number|Date} date - Data
 * @returns {string} Tempo relativo formatado
 */
export const formatRelativeTime = (date) => {
  if (!date) return "";

  const now = new Date();
  const targetDate = new Date(date);
  const diffMs = now - targetDate;
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffSecs < 60) return "agora";
  if (diffMins < 60) return `há ${diffMins} min`;
  if (diffHours < 24) return `há ${diffHours}h`;
  if (diffDays === 1) return "há 1 dia";
  if (diffDays < 7) return `há ${diffDays} dias`;
  if (diffDays < 30) return `há ${Math.floor(diffDays / 7)} semanas`;

  return targetDate.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

/**
 * Formata data simples
 * @param {string|number|Date} date - Data
 * @returns {string} Data formatada
 */
export const formatDate = (date) => {
  if (!date) return "";

  const targetDate = new Date(date);
  return targetDate.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};
